import { Component, Input, OnInit } from '@angular/core';
import { SelectItem } from '../header-menu-item/header-menu-item.component';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormModule, InputMaskType, MaskedOptions } from '@rogers/cdk/form';
import { ButtonModule } from '@rogers/cdk/button';
import { CommonModule } from '@angular/common';
import { CreditCardComponent } from '../credit-card/credit-card.component';

export enum FormFieldType {
  INPUT = 'text',
  SELECT = 'select',
  CREDIT = 'credit',
}

export interface ReactiveError {
  error: string;
  message: string;
}

export interface FormFieldData {
  type: FormFieldType;
  id: string;
  name: string;
  mask: InputMaskType;
  customMask?: MaskedOptions;
  placeholder?: string;
  label?: string;
  value?: string;
  options?: SelectItem[];
  reactiveErrors?: ReactiveError[];
  minLength?: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormModule,
    ButtonModule,
    CreditCardComponent,
  ],
  selector: 'ucx-form-fields',
  templateUrl: './form-fields.component.html',
  styleUrls: ['./form-fields.component.scss'],
})
export class FormFieldsComponent implements OnInit {
  @Input() formFields: FormFieldData[] = [];
  @Input() okBtnText = 'OK';
  @Input() cancelBtnText = 'CANCEL';
  @Input() isSubmitBtnDisabled = false;
  @Input() isCancelBtnHidden = false;
  @Input() creditCardConfig: any;

  @Input() onHandleSubmit;
  @Input() onHandleCancel;

  formGroup: FormGroup | undefined;
  formFieldType = FormFieldType;
  isCreditCard = false;

  ngOnInit() {
    const controls = {};
    this.formFields.forEach((field) => {
      const validators = [];
      if (field.reactiveErrors && field.reactiveErrors.length) {
        if (field.reactiveErrors.find((item) => item.error == 'required')) {
          validators.push(Validators.required);
        }
        if (field.reactiveErrors.find((item) => item.error == 'email')) {
          validators.push(Validators.email);
        }
      }
      //TODO Need to implement all field types
      if (
        field.type == FormFieldType.INPUT ||
        field.type == FormFieldType.SELECT
      ) {
        controls[field.name] = new FormControl(field.value ?? '', validators);
      }
    });
    this.formGroup = new FormGroup(controls);
    this.isCreditCard = this.formFields.some(
      (item) => item.type == FormFieldType.CREDIT
    );
  }

  onFormSubmit(): void {
    if (this.formGroup?.valid) {
      this.isSubmitBtnDisabled = true;
      let result = this.formGroup.value;
      if (this.isCreditCard) {
        const mappedCC = {
          'Credit Card Type / Type de carte de crédit':
            this.formGroup?.get('creditCardNumber')?.value?.creditCardType,
          'Credit Card Number / Numéro de carte de crédit':
            this.formGroup?.get('creditCardNumber')?.value?.token,
          'Credit Card Expiry Date / Date d’expiration de la carte de crédit':
            this.formGroup?.get('expiryDate')?.value,
          'CVV Number / Numéro CVV': this.formGroup?.get('cvv')?.value,
        };
        result = { ...this.formGroup.value, ...mappedCC };
      }
      this.onHandleSubmit(result);
    }
  }

  onCancel = (): void => {
    this.onHandleCancel();
  };

  getErrorMessage(fomControl: FormControl, field: FormFieldData): string {
    let message = '';
    const errors = fomControl?.errors;
    if (errors && field.reactiveErrors && field.reactiveErrors.length) {
      const err = Object.keys(errors)[0] || '';
      const reactiveError = field.reactiveErrors.find((e) => e.error === err);
      if (reactiveError) {
        message = reactiveError.message;
      }
    }
    return message;
  }

  getFormControl(fieldName: string): FormControl {
    return this.formGroup?.get(fieldName) as FormControl;
  }

  /* Prevent the enter key from submitting the form */
  onInputEnterKey(): boolean {
    return false;
  }
}
